import React, { useState, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Avatar } from './Avatar';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import { API_BASE_URL } from '../../../services/api';

const API_URL = process.env.REACT_APP_API_URL || `${API_BASE_URL}/api`;

export const CustomerDetailsDrawer = ({
  isOpen,
  onClose,
  conversation,
  messages,
  leadData,
  loadingLead,
  onOpenAssign,
  currentUser
}) => {
  const { token } = useAuthStore();
  const isOwnerOrAdmin = ['owner', 'admin', 'superadmin'].includes((currentUser?.role || '').toLowerCase());
  
  const [shortNote, setShortNote] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isLoadingNote, setIsLoadingNote] = useState(false);

  useEffect(() => {
    if (isOpen && conversation?.customerPhone) {
      fetchNote();
    }
  }, [isOpen, conversation?.customerPhone]);

  const fetchNote = async () => {
    try {
      setIsLoadingNote(true);
      const cleanPhone = conversation.customerPhone.replace('+', '');
      const res = await axios.get(`${API_URL}/whatsapp/notes/${cleanPhone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShortNote(res.data.data?.note || '');
    } catch (error) {
      console.error('Failed to fetch note:', error);
    } finally {
      setIsLoadingNote(false);
    }
  };

  const handleSaveNote = async () => {
    try {
      setIsSavingNote(true);
      const cleanPhone = conversation.customerPhone.replace('+', '');
      await axios.post(`${API_URL}/whatsapp/notes`, {
        customerPhone: cleanPhone,
        note: shortNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note saved successfully');
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setIsSavingNote(false);
    }
  };

  const customerName = conversation?.customerName !== 'Unknown' 
    ? conversation?.customerName 
    : conversation?.customerPhone;

  const totalMessages = messages?.length || 0;
  const unreadMessages = conversation?.unreadCount || 0;
  const firstMessageDate = messages?.[0]?.timestamp;
  const lastMessageDate = conversation?.lastMessageAt;

  const actualLead = leadData?.not_found ? null : leadData;

  const handleNavigate = (path) => {
    // In a real router, you'd use useNavigate() hook here.
    // Assuming standard hrefs or window.location for now.
    window.location.href = path;
  };

  const getTags = () => {
    const tags = [];
    if (!actualLead) return tags;
    
    if (actualLead.status === 'new' || actualLead.status === 'New') tags.push('New Lead');
    if (actualLead.status === 'hot' || actualLead.leadStatus === 'Hot') tags.push('Hot Lead');
    if (actualLead.status === 'site_visit_done' || actualLead.leadStatus === 'Site Visit Done') tags.push('Site Visit Done');
    if (actualLead.quotationAmount > 0) tags.push('Quotation Sent');
    if (actualLead.projectStatus && actualLead.projectStatus !== 'Not started') tags.push('Project Started');

    if (tags.length === 0) tags.push(actualLead.status || 'Active');
    
    return tags;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ease-in-out md:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div 
        className={`fixed right-0 top-0 h-full w-full md:w-96 bg-[#F8FAFC] shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col border-l border-gray-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Sticky Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
          <h2 className="text-lg font-bold text-gray-800">Contact Details</h2>
          <button 
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {/* Customer Profile Banner */}
          <div className="flex flex-col items-center text-center">
            <Avatar name={customerName} size="xl" className="ring-4 ring-white shadow-md mb-4" active={conversation?.status === 'open'} />
            <h3 className="text-xl font-bold text-gray-900 mb-1">{customerName}</h3>
            <p className="text-sm font-medium text-gray-500 flex items-center justify-center gap-1.5 mb-4">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              {conversation?.customerPhone}
            </p>

            {loadingLead ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                Fetching ERP Data...
              </div>
            ) : actualLead ? (
              <div className="flex flex-wrap justify-center gap-2">
                {getTags().map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider rounded-full border border-purple-200">
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                Not recorded in Leads
              </span>
            )}
          </div>

          {/* Quick Statistics Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
              <span className="text-2xl font-bold text-gray-800 mb-1">
                💬 {totalMessages}
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Messages</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
              <span className="text-2xl font-bold text-gray-800 mb-1">
                👥 {conversation?.assignedTo?.length || 0}
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigned Staff</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
              <span className="text-sm font-bold text-gray-800 mb-1 break-words w-full px-1">
                📅 {lastMessageDate ? formatDistanceToNow(new Date(lastMessageDate), { addSuffix: true }) : 'Never'}
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-1">Last Activity</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center opacity-70">
              <span className="text-lg font-bold text-gray-800 mb-1">
                ⏱️ N/A
              </span>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-1">Response Time</span>
            </div>
          </div>

          {/* ERP Customer Information (If found) */}
          {actualLead && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">ERP Profile</h4>
              <div className="space-y-4">
                {actualLead.customId && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Lead ID</p>
                    <p className="text-sm font-semibold text-gray-800">{actualLead.customId}</p>
                  </div>
                )}
                {actualLead.email && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Email Address</p>
                    <p className="text-sm font-semibold text-gray-800">{actualLead.email}</p>
                  </div>
                )}
                {actualLead.propertyType && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Project Name / Property</p>
                    <p className="text-sm font-semibold text-gray-800">{actualLead.propertyType}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Created Date</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {actualLead.createdAt ? format(new Date(actualLead.createdAt), 'PPP') : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Customer Journey Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-5">Customer Journey</h4>
            <div className="relative pl-4 border-l-2 border-gray-200 space-y-6">
              {actualLead && (
                <div className="relative">
                  <div className="absolute -left-[21px] bg-purple-500 h-3 w-3 rounded-full ring-4 ring-white" />
                  <p className="text-sm font-bold text-gray-800">Lead Created</p>
                  <p className="text-xs text-gray-500 mt-1">{actualLead.createdAt ? format(new Date(actualLead.createdAt), 'PP p') : 'System recorded'}</p>
                </div>
              )}
              {firstMessageDate && (
                <div className="relative">
                  <div className="absolute -left-[21px] bg-green-500 h-3 w-3 rounded-full ring-4 ring-white" />
                  <p className="text-sm font-bold text-gray-800">First WhatsApp Message</p>
                  <p className="text-xs text-gray-500 mt-1">{format(new Date(firstMessageDate), 'PP p')}</p>
                </div>
              )}
              {conversation?.assignedTo?.length > 0 && (
                <div className="relative">
                  <div className="absolute -left-[21px] bg-blue-500 h-3 w-3 rounded-full ring-4 ring-white" />
                  <p className="text-sm font-bold text-gray-800">Staff Assigned</p>
                  <p className="text-xs text-gray-500 mt-1">{conversation.assignedTo.length} members managing chat</p>
                </div>
              )}
              {lastMessageDate && (
                <div className="relative">
                  <div className="absolute -left-[21px] bg-gray-400 h-3 w-3 rounded-full ring-4 ring-white" />
                  <p className="text-sm font-bold text-gray-800">Last Activity</p>
                  <p className="text-xs text-gray-500 mt-1">{format(new Date(lastMessageDate), 'PP p')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assigned Employees List */}
          {Array.isArray(conversation?.assignedTo) && conversation.assignedTo.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">Assigned Team</h4>
              <div className="space-y-3">
                {conversation.assignedTo.map((emp) => (
                  <div key={emp._id || emp.id} className="flex items-center gap-3 group">
                    <Avatar name={`${emp.firstName} ${emp.lastName}`} size="md" active={true} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{emp.firstName} {emp.lastName}</p>
                      <p className="text-xs font-medium text-gray-500 capitalize truncate">{emp.role?.replace('_', ' ') || 'Employee'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-2 pb-6">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 px-1">Quick Actions</h4>
            
            {isOwnerOrAdmin && (
              <button 
                onClick={onOpenAssign}
                className="w-full flex items-center justify-between p-3.5 bg-white border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-purple-200 transition-colors group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-lg group-hover:bg-purple-200 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  </div>
                  <span className="font-semibold text-gray-800 group-hover:text-purple-700">Assign Employees</span>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
              </button>
            )}

            {actualLead && (
              <>
                <button 
                  onClick={() => handleNavigate(`/verve-leads/${actualLead._id}`)}
                  className="w-full flex items-center justify-between p-3.5 bg-white border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-colors group shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <span className="font-semibold text-gray-800 group-hover:text-blue-700">Open Lead Profile</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </button>

                <button 
                  onClick={() => handleNavigate(`/verve-contacts/${actualLead._id}`)}
                  className="w-full flex items-center justify-between p-3.5 bg-white border border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-200 transition-colors group shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 text-orange-600 rounded-lg group-hover:bg-orange-200 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                    </div>
                    <span className="font-semibold text-gray-800 group-hover:text-orange-700">Open Contact details</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </button>
              </>
            )}

            {/* <button 
              disabled
              className="w-full flex items-center justify-between p-3.5 bg-gray-50 border border-gray-200 rounded-xl cursor-not-allowed opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 text-gray-600 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <span className="font-semibold text-gray-600">Start Audio Call</span>
              </div>
              <span className="text-xs font-bold bg-gray-200 px-2 py-1 rounded text-gray-500">Coming Soon</span>
            </button>

            <button 
              disabled
              className="w-full flex items-center justify-between p-3.5 bg-gray-50 border border-gray-200 rounded-xl cursor-not-allowed opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 text-gray-600 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </div>
                <span className="font-semibold text-gray-600">Start Video Call</span>
              </div>
              <span className="text-xs font-bold bg-gray-200 px-2 py-1 rounded text-gray-500">Coming Soon</span>
            </button> */}

            <div className="mt-6">
              <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2 px-1 flex justify-between items-center">
                Short Notes
                {isLoadingNote && <span className="text-xs font-normal text-gray-500 normal-case animate-pulse">Loading...</span>}
              </h4>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent transition-all">
                <textarea 
                  value={shortNote}
                  onChange={(e) => setShortNote(e.target.value)}
                  placeholder="Write a quick note about this customer..."
                  className="w-full p-4 text-sm text-gray-800 bg-transparent border-none resize-none focus:outline-none placeholder-gray-400 min-h-[100px]"
                />
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 flex justify-end">
                  <button 
                    onClick={handleSaveNote}
                    disabled={isSavingNote || isLoadingNote}
                    className="flex items-center gap-2 px-4 py-1.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {isSavingNote ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                        Save Note
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};
